import { Clapperboard } from "lucide-react";
import { z } from "zod";
import {
  HarmonyBookClosed,
  HarmonyImage,
} from "@/components/icons/harmony-icons-inline";
import {
  routeHandle,
  type ModelsParams,
  type ModuleBlueprint,
} from "../registry/types";

/**
 * `models` blueprint — Model Network: generative/LLM modeller, wizard,
 * kütüphane ve video stüdyosu. Parametresiz.
 *
 * Segment sözlüğü (basePath'e göre):
 *   ""                    → modül kokpiti (deste, üretimler, girişler)
 *   explore               → model dizini (eski /ai-models ve /)
 *   wizard/:id/:chatId?   → model wizard (eski /wizard/...)
 *   library               → kütüphane (eski /library)
 *   studio/projects       → stüdyo projeleri (eski /studio/projects) [bare]
 *   studio                → video editör (eski /studio)              [bare]
 */

export const MODELS_SEGMENTS = {
  explore: "explore",
  wizard: "wizard",
  library: "library",
  studio: "studio",
  studioProjects: "studio/projects",
} as const;

const modelsParamsSchema: z.ZodType<ModelsParams> = z
  .object({})
  .strict() as unknown as z.ZodType<ModelsParams>;

export const modelsBlueprint: ModuleBlueprint<ModelsParams> = {
  id: "models",
  paramsSchema: modelsParamsSchema,
  chat: { enabled: true, agentId: "models" },

  routes: () => [
    {
      index: true,
      lazy: async () => ({
        Component: (await import("@/features/models-landing")).ModelsLandingPage,
      }),
      // Misafir kokpiti görür; kişisel bloklar oturum ister
      handle: routeHandle({ auth: "optional" }),
    },
    {
      path: MODELS_SEGMENTS.explore,
      lazy: async () => ({
        Component: (await import("@/pages/AIModelsPage")).AIModelsPage,
      }),
      // Guest de model keşfedebilir; aksiyonlar AuthGateDialog ile korunur
      handle: routeHandle({ auth: "optional" }),
    },
    {
      path: `${MODELS_SEGMENTS.wizard}/:id/:chatId?`,
      lazy: async () => ({
        Component: (await import("@/pages/WizardPage")).WizardPage,
      }),
      handle: routeHandle({ auth: "optional" }),
    },
    {
      path: MODELS_SEGMENTS.library,
      lazy: async () => ({
        Component: (await import("@/pages/LibraryPage")).default,
      }),
      handle: routeHandle({ auth: "private" }),
    },
    {
      path: MODELS_SEGMENTS.studioProjects,
      lazy: async () => ({
        Component: (await import("@/pages/EditorProjectsPage")).default,
      }),
      handle: routeHandle({ auth: "private", layout: "bare" }),
    },
    {
      path: MODELS_SEGMENTS.studio,
      lazy: async () => ({
        Component: (await import("@/pages/VideoEditorPage")).default,
      }),
      handle: routeHandle({ auth: "private", layout: "bare" }),
    },
  ],

  nav: () => [
    {
      id: "models",
      labelKey: "common.aiModels",
      icon: HarmonyBookClosed,
      to: "",
    },
    {
      id: "library",
      labelKey: "common.library",
      icon: HarmonyImage,
      to: MODELS_SEGMENTS.library,
    },
    {
      id: "studio",
      labelKey: "editorProjects.navTitle",
      icon: Clapperboard,
      to: MODELS_SEGMENTS.studioProjects,
    },
  ],
};
